import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { 
  Inbox, Send, CheckCircle, XCircle, Plus, Eye, Loader2, 
  Search, FileText, Calendar, DollarSign, Clock, User,
  ArrowUpDown, ArrowUp, ArrowDown, Trash2, Download, Building, Briefcase,
  Upload, File, X, History
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('fortis_token');
  return token ? { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } : {};
};

const AccountingRequests = () => {
  const [activeTab, setActiveTab] = useState('inbox');
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Data states
  const [inboxRequests, setInboxRequests] = useState([]);
  const [myRequests, setMyRequests] = useState([]);
  const [approvedRequests, setApprovedRequests] = useState([]);
  const [rejectedRequests, setRejectedRequests] = useState([]);
  const [convertedToJobRequests, setConvertedToJobRequests] = useState([]);
  const [branches, setBranches] = useState([]);
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showActionModal, setShowActionModal] = useState(false);
  const [showAddToJobsModal, setShowAddToJobsModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [actionType, setActionType] = useState('');
  const [actionNote, setActionNote] = useState('');
  const [addToJobsProcessing, setAddToJobsProcessing] = useState(false);
  
  // İş formu - AccountingJobs ile aynı yapı
  const [jobFormData, setJobFormData] = useState({
    konu: '',
    aciklama: '',
    sube_id: '',
    belgeler: []
  });
  
  // Sorting
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  
  // Create form
  const [formData, setFormData] = useState({
    request_type: 'genel',
    title: '',
    description: '',
    amount: '',
    currency: 'TRY',
    due_date: '',
    priority: 'normal'
  });

  // Fetch functions
  const fetchInboxRequests = useCallback(async () => {
    try {
      const response = await fetch(`${API}/accounting/requests/inbox`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setInboxRequests(data);
      }
    } catch (error) {
      console.error('Gelen talepler yüklenemedi:', error);
    }
  }, []);

  const fetchMyRequests = useCallback(async () => {
    try {
      const response = await fetch(`${API}/accounting/requests/my`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setMyRequests(data);
      }
    } catch (error) {
      console.error('Taleplerim yüklenemedi:', error);
    }
  }, []);

  const fetchApprovedRequests = useCallback(async () => {
    try {
      const response = await fetch(`${API}/accounting/requests/approved`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setApprovedRequests(data);
      }
    } catch (error) {
      console.error('Onaylanan talepler yüklenemedi:', error);
    }
  }, []);

  const fetchRejectedRequests = useCallback(async () => {
    try {
      const response = await fetch(`${API}/accounting/requests/rejected`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setRejectedRequests(data);
      }
    } catch (error) {
      console.error('Reddedilen talepler yüklenemedi:', error);
    }
  }, []);

  const fetchConvertedToJobRequests = useCallback(async () => {
    try {
      const response = await fetch(`${API}/accounting/requests/converted-to-job`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setConvertedToJobRequests(data);
      }
    } catch (error) {
      console.error('İşe dönüştürülen talepler yüklenemedi:', error);
    }
  }, []);

  const fetchBranches = useCallback(async () => {
    try {
      const response = await fetch(`${API}/branches`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setBranches(data);
      }
    } catch (error) {
      console.error('Şubeler yüklenemedi:', error);
    }
  }, []);

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    await Promise.all([
      fetchInboxRequests(),
      fetchMyRequests(),
      fetchApprovedRequests(),
      fetchRejectedRequests(),
      fetchConvertedToJobRequests(),
      fetchBranches()
    ]);
    setLoading(false);
  }, [fetchInboxRequests, fetchMyRequests, fetchApprovedRequests, fetchRejectedRequests, fetchConvertedToJobRequests, fetchBranches]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  // Create request
  const handleCreateRequest = async () => {
    if (!formData.title.trim()) {
      toast.error('Başlık zorunludur');
      return;
    }

    try {
      const response = await fetch(`${API}/accounting/requests`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success('Talep oluşturuldu');
        setShowCreateModal(false);
        setFormData({
          request_type: 'genel',
          title: '',
          description: '',
          amount: '',
          currency: 'TRY',
          due_date: '',
          priority: 'normal'
        });
        fetchAllData();
      } else {
        toast.error('Talep oluşturulamadı');
      }
    } catch (error) {
      toast.error('Bir hata oluştu');
    }
  };

  // Approve/Reject request
  const handleAction = async () => {
    if (!selectedRequest) return;

    const endpoint = actionType === 'approve' 
      ? `${API}/accounting/requests/${selectedRequest.id}/approve`
      : `${API}/accounting/requests/${selectedRequest.id}/reject`;

    const body = actionType === 'approve' 
      ? { note: actionNote }
      : { reason: actionNote };

    try {
      const response = await fetch(endpoint, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(body)
      });

      if (response.ok) {
        toast.success(actionType === 'approve' ? 'Talep onaylandı' : 'Talep reddedildi');
        setShowActionModal(false);
        setActionNote('');
        fetchAllData();
      } else {
        toast.error('İşlem başarısız');
      }
    } catch (error) {
      toast.error('Bir hata oluştu');
    }
  };

  // Delete request
  const handleDelete = async (requestId) => {
    if (!window.confirm('Bu talebi silmek istediğinizden emin misiniz?')) return;

    try {
      const response = await fetch(`${API}/accounting/requests/${requestId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        toast.success('Talep silindi');
        fetchAllData();
      } else {
        toast.error('Silme işlemi başarısız');
      }
    } catch (error) {
      toast.error('Bir hata oluştu');
    }
  };

  // İş Olarak Ekle modalını aç - AccountingJobs formu ile aynı
  const openAddToJobsModal = (request) => {
    setSelectedRequest(request);
    setJobFormData({
      konu: request.title || '',
      aciklama: request.description || '',
      sube_id: request.branch_id || '',
      belgeler: []
    });
    setShowAddToJobsModal(true);
  };

  // Belge yükleme
  const handleJobFileUpload = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = () => {
        setJobFormData(prev => ({
          ...prev,
          belgeler: [...prev.belgeler, {
            name: file.name,
            type: file.type,
            size: file.size,
            data: reader.result
          }]
        }));
      };
      reader.readAsDataURL(file);
    });
  };

  // Belge kaldır
  const removeJobFile = (index) => {
    setJobFormData(prev => ({
      ...prev,
      belgeler: prev.belgeler.filter((_, i) => i !== index)
    }));
  };

  // İş Olarak Ekle işlemi - AccountingJobs ile aynı yapı
  const handleAddToJobs = async () => {
    if (!jobFormData.konu.trim()) {
      toast.error('İşin konusu zorunludur');
      return;
    }

    setAddToJobsProcessing(true);
    try {
      // Muhasebe işi oluştur - AccountingJobs formatında
      const jobData = {
        konu: jobFormData.konu,
        aciklama: jobFormData.aciklama,
        sube_id: jobFormData.sube_id || null,
        belgeler: jobFormData.belgeler,
        source_request_id: selectedRequest.id,
        source_request: {
          id: selectedRequest.id,
          title: selectedRequest.title,
          description: selectedRequest.description,
          requester_name: selectedRequest.requester_name || selectedRequest.owner_name,
          created_at: selectedRequest.created_at,
          attachments: selectedRequest.attachments,
          documents: selectedRequest.documents
        }
      };

      const response = await fetch(`${API}/accounting/isler`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(jobData)
      });

      if (response.ok) {
        // Talebin durumunu "converted_to_job" olarak güncelle
        await fetch(`${API}/accounting/requests/${selectedRequest.id}/convert-to-job`, {
          method: 'POST',
          headers: getAuthHeaders()
        });
        
        toast.success('Talep işlere eklendi');
        setShowAddToJobsModal(false);
        setShowDetailModal(false);
        fetchAllData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'İşlere eklenemedi');
      }
    } catch (error) {
      console.error('İşlere ekleme hatası:', error);
      toast.error('Bir hata oluştu');
    } finally {
      setAddToJobsProcessing(false);
    }
  };

  // Sorting
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const sortData = (data) => {
    if (!data || data.length === 0) return [];
    return [...data].sort((a, b) => {
      const aVal = a[sortConfig.key] || '';
      const bVal = b[sortConfig.key] || '';
      if (sortConfig.direction === 'asc') {
        return aVal > bVal ? 1 : -1;
      }
      return aVal < bVal ? 1 : -1;
    });
  };

  // Filter by search
  const filterData = (data) => {
    if (!searchTerm) return data;
    const term = searchTerm.toLowerCase();
    return data.filter(item => 
      item.title?.toLowerCase().includes(term) ||
      item.requester_name?.toLowerCase().includes(term) ||
      item.description?.toLowerCase().includes(term)
    );
  };

  // Helper functions
  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: 'Beklemede', className: 'bg-yellow-100 text-yellow-700 border-yellow-300' },
      approved: { label: 'Onaylandı', className: 'bg-green-100 text-green-700 border-green-300' },
      rejected: { label: 'Reddedildi', className: 'bg-red-100 text-red-700 border-red-300' }
    };
    const config = statusConfig[status] || statusConfig.pending;
    return <Badge variant="outline" className={config.className}>{config.label}</Badge>;
  };

  const getPriorityBadge = (priority) => {
    const priorityConfig = {
      low: { label: 'Düşük', className: 'bg-gray-100 text-gray-700' },
      normal: { label: 'Normal', className: 'bg-blue-100 text-blue-700' },
      high: { label: 'Yüksek', className: 'bg-orange-100 text-orange-700' },
      urgent: { label: 'Acil', className: 'bg-red-100 text-red-700' }
    };
    const config = priorityConfig[priority] || priorityConfig.normal;
    return <Badge variant="outline" className={config.className}>{config.label}</Badge>;
  };

  const getRequestTypeBadge = (type) => {
    const typeConfig = {
      fatura: { label: 'Fatura', className: 'bg-purple-100 text-purple-700' },
      odeme: { label: 'Ödeme', className: 'bg-indigo-100 text-indigo-700' },
      masraf: { label: 'Masraf', className: 'bg-pink-100 text-pink-700' },
      avans: { label: 'Avans', className: 'bg-cyan-100 text-cyan-700' },
      genel: { label: 'Genel', className: 'bg-gray-100 text-gray-700' }
    };
    const config = typeConfig[type] || typeConfig.genel;
    return <Badge variant="outline" className={config.className}>{config.label}</Badge>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('tr-TR');
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return { date: '-', time: '' };
    const date = new Date(dateStr);
    return {
      date: date.toLocaleDateString('tr-TR'),
      time: date.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })
    };
  };

  const formatCurrency = (amount, currency = 'TRY') => {
    if (!amount) return '-';
    return new Intl.NumberFormat('tr-TR', { style: 'currency', currency }).format(amount);
  };

  const SortableHeader = ({ field, children }) => (
    <TableHead 
      className="cursor-pointer hover:bg-gray-50 select-none"
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center gap-1">
        {children}
        {sortConfig.key === field ? (
          sortConfig.direction === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
        ) : (
          <ArrowUpDown className="h-4 w-4 opacity-30" />
        )}
      </div>
    </TableHead>
  );

  // Table component
  const RequestsTable = ({ data, showActions = false, showHistory = false }) => (
    <div className="border rounded-lg overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="bg-gray-50">
            <SortableHeader field="created_at">Tarih</SortableHeader>
            <SortableHeader field="branch_name">Şube</SortableHeader>
            <SortableHeader field="title">Talebin Konusu</SortableHeader>
            <SortableHeader field="description">Açıklama</SortableHeader>
            <TableHead>Belgeler</TableHead>
            <TableHead>Durum</TableHead>
            <TableHead className="text-right">İşlemler</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                Talep bulunamadı
              </TableCell>
            </TableRow>
          ) : (
            sortData(filterData(data)).map((request) => (
              <TableRow key={request.id} className="hover:bg-gray-50">
                <TableCell>
                  <div className="flex flex-col text-sm">
                    <div className="flex items-center gap-1 text-gray-700">
                      <Calendar className="h-4 w-4 text-gray-400" />
                      {formatDateTime(request.created_at).date}
                    </div>
                    <div className="flex items-center gap-1 text-gray-500 text-xs ml-5">
                      <Clock className="h-3 w-3 text-gray-400" />
                      {formatDateTime(request.created_at).time}
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <div className="h-6 w-6 rounded bg-indigo-100 flex items-center justify-center">
                      <span className="text-xs font-bold text-indigo-600">
                        {(request.branch_name || request.owner_name || '-').charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <span className="text-sm">{request.branch_name || '-'}</span>
                  </div>
                </TableCell>
                <TableCell className="font-medium max-w-[200px]">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-amber-500" />
                    <span className="truncate">{request.title || '-'}</span>
                  </div>
                </TableCell>
                <TableCell className="max-w-[200px]">
                  <span className="text-sm text-gray-600 truncate block">{request.description || '-'}</span>
                </TableCell>
                <TableCell>
                  {(request.attachments?.length > 0 || request.documents?.length > 0) ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="p-0 h-auto"
                      onClick={() => {
                        setSelectedRequest(request);
                        setShowDetailModal(true);
                      }}
                    >
                      <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 cursor-pointer hover:bg-blue-100">
                        <FileText className="h-3 w-3 mr-1" />
                        {(request.attachments?.length || 0) + (request.documents?.length || 0)} Belge
                      </Badge>
                    </Button>
                  ) : (
                    <span className="text-gray-400 text-sm">-</span>
                  )}
                </TableCell>
                <TableCell>{getStatusBadge(request.status)}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setSelectedRequest(request);
                        setShowDetailModal(true);
                      }}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    {showActions && (request.status === 'pending' || request.status === 'pending_muhasebe' || request.status === 'forwarded_to_accounting') && (
                      <>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-green-600 hover:text-green-700 hover:bg-green-50"
                          onClick={() => {
                            setSelectedRequest(request);
                            setActionType('approve');
                            setShowActionModal(true);
                          }}
                          title="Onayla"
                        >
                          <CheckCircle className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => {
                            setSelectedRequest(request);
                            setActionType('reject');
                            setShowActionModal(true);
                          }}
                          title="Reddet"
                        >
                          <XCircle className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                    {!showActions && request.status === 'pending' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => handleDelete(request.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                    {showHistory && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50"
                        onClick={() => {
                          setSelectedRequest(request);
                          setShowHistoryModal(true);
                        }}
                        title="Talep Geçmişi"
                      >
                        <History className="h-4 w-4 mr-1" />
                        Geçmiş
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="accounting-requests">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Talepler</h1>
          <p className="text-gray-500">Muhasebe taleplerini yönetin</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)} className="gap-2">
          <Plus className="h-4 w-4" />
          Yeni Talep
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          placeholder="Talep ara..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="inbox" className="gap-2">
            <Inbox className="h-4 w-4" />
            Gelen Kutusu
            {inboxRequests.length > 0 && (
              <Badge variant="secondary" className="ml-1">{inboxRequests.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="my" className="gap-2">
            <Send className="h-4 w-4" />
            Taleplerim
            {myRequests.length > 0 && (
              <Badge variant="secondary" className="ml-1">{myRequests.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="converted" className="gap-2">
            <Briefcase className="h-4 w-4" />
            İşe Dönüştürülen
            {convertedToJobRequests.length > 0 && (
              <Badge variant="secondary" className="ml-1">{convertedToJobRequests.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="approved" className="gap-2">
            <CheckCircle className="h-4 w-4" />
            Onaylanan
          </TabsTrigger>
          <TabsTrigger value="rejected" className="gap-2">
            <XCircle className="h-4 w-4" />
            Reddedilen
          </TabsTrigger>
        </TabsList>

        <TabsContent value="inbox" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Inbox className="h-5 w-5" />
                Gelen Talepler
              </CardTitle>
            </CardHeader>
            <CardContent>
              <RequestsTable data={inboxRequests} showActions={true} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="my" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Send className="h-5 w-5" />
                Gönderdiğim Talepler
              </CardTitle>
            </CardHeader>
            <CardContent>
              <RequestsTable data={myRequests} showActions={false} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="approved" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-green-600">
                <CheckCircle className="h-5 w-5" />
                Onaylanan Talepler
              </CardTitle>
            </CardHeader>
            <CardContent>
              <RequestsTable data={approvedRequests} showActions={false} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="converted" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-indigo-600">
                <Briefcase className="h-5 w-5" />
                İşe Dönüştürülen Talepler
              </CardTitle>
            </CardHeader>
            <CardContent>
              <RequestsTable data={convertedToJobRequests} showActions={false} showHistory={true} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="rejected" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-600">
                <XCircle className="h-5 w-5" />
                Reddedilen Talepler
              </CardTitle>
            </CardHeader>
            <CardContent>
              <RequestsTable data={rejectedRequests} showActions={false} />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5" />
              Yeni Talep Oluştur
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Talep Türü *</Label>
              <Select value={formData.request_type} onValueChange={(v) => setFormData({...formData, request_type: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Tür seçin" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="genel">Genel</SelectItem>
                  <SelectItem value="fatura">Fatura</SelectItem>
                  <SelectItem value="odeme">Ödeme</SelectItem>
                  <SelectItem value="masraf">Masraf</SelectItem>
                  <SelectItem value="avans">Avans</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Başlık *</Label>
              <Input 
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                placeholder="Talep başlığı"
              />
            </div>
            <div className="space-y-2">
              <Label>Açıklama</Label>
              <Textarea 
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Detaylı açıklama"
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tutar</Label>
                <div className="flex gap-2">
                  <Input 
                    type="number"
                    value={formData.amount}
                    onChange={(e) => setFormData({...formData, amount: e.target.value})}
                    placeholder="0.00"
                    className="flex-1"
                  />
                  <Select value={formData.currency} onValueChange={(v) => setFormData({...formData, currency: v})}>
                    <SelectTrigger className="w-24">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="TRY">TRY</SelectItem>
                      <SelectItem value="USD">USD</SelectItem>
                      <SelectItem value="EUR">EUR</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Öncelik</Label>
                <Select value={formData.priority} onValueChange={(v) => setFormData({...formData, priority: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Düşük</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">Yüksek</SelectItem>
                    <SelectItem value="urgent">Acil</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Son Tarih</Label>
              <Input 
                type="date"
                value={formData.due_date}
                onChange={(e) => setFormData({...formData, due_date: e.target.value})}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>İptal</Button>
            <Button onClick={handleCreateRequest}>Oluştur</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="sm:max-w-[550px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-amber-500" />
              Talep Detayı
            </DialogTitle>
          </DialogHeader>
          {selectedRequest && (
            <div className="space-y-4 py-4">
              <div className="flex items-center gap-2">
                {getStatusBadge(selectedRequest.status)}
              </div>
              
              {/* Başlık ve Açıklama */}
              <div>
                <h3 className="font-semibold text-lg">{selectedRequest.title}</h3>
                <p className="text-gray-600 mt-1">{selectedRequest.description || 'Açıklama yok'}</p>
              </div>
              
              {/* Şube ve Tarih Bilgileri */}
              <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                <div>
                  <Label className="text-gray-500">Şube</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Building className="h-4 w-4 text-gray-400" />
                    <p className="font-medium">{selectedRequest.branch_name || '-'}</p>
                  </div>
                </div>
                <div>
                  <Label className="text-gray-500">Talep Eden</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <User className="h-4 w-4 text-gray-400" />
                    <p className="font-medium">{selectedRequest.requester_name || selectedRequest.owner_name || '-'}</p>
                  </div>
                </div>
                <div>
                  <Label className="text-gray-500">Oluşturulma Tarihi</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Calendar className="h-4 w-4 text-gray-400" />
                    <p className="font-medium">{formatDate(selectedRequest.created_at)}</p>
                  </div>
                </div>
              </div>
              
              {/* Belgeler */}
              {((selectedRequest.attachments && selectedRequest.attachments.length > 0) || 
                (selectedRequest.documents && selectedRequest.documents.length > 0)) && (
                <div className="pt-4 border-t">
                  <Label className="text-gray-500 mb-2 block">Belgeler ({(selectedRequest.attachments?.length || 0) + (selectedRequest.documents?.length || 0)})</Label>
                  <div className="space-y-2">
                    {[...(selectedRequest.attachments || []), ...(selectedRequest.documents || [])].map((doc, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-amber-100 rounded-lg">
                            <FileText className="h-5 w-5 text-amber-600" />
                          </div>
                          <div>
                            <span className="text-sm font-medium block">{doc.name || doc.filename || 'Belge'}</span>
                            {doc.size && (
                              <span className="text-xs text-gray-400">
                                {(doc.size / 1024).toFixed(1)} KB
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {/* URL veya data (base64) varsa butonları göster */}
                          {(doc.url || doc.data) && (
                            <>
                              {/* Önizle butonu - sadece resimler için */}
                              {(doc.type?.startsWith('image/') || doc.name?.match(/\.(jpg|jpeg|png|gif|webp)$/i)) && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    const imgSrc = doc.url || doc.data;
                                    const modal = document.createElement('div');
                                    modal.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,0.8);display:flex;align-items:center;justify-content:center;z-index:9999;cursor:pointer';
                                    modal.onclick = () => modal.remove();
                                    const img = document.createElement('img');
                                    img.src = imgSrc;
                                    img.style.cssText = 'max-width:90%;max-height:90%;object-fit:contain;border-radius:8px';
                                    modal.appendChild(img);
                                    document.body.appendChild(modal);
                                  }}
                                  className="text-blue-600 hover:text-blue-700"
                                  data-testid={`preview-doc-${idx}`}
                                >
                                  <Eye className="h-4 w-4 mr-1" />
                                  Önizle
                                </Button>
                              )}
                              {/* PDF için görüntüle */}
                              {(doc.type === 'application/pdf' || doc.name?.match(/\.pdf$/i)) && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    const pdfSrc = doc.url || doc.data;
                                    window.open(pdfSrc, '_blank');
                                  }}
                                  className="text-blue-600 hover:text-blue-700"
                                  data-testid={`view-pdf-${idx}`}
                                >
                                  <Eye className="h-4 w-4 mr-1" />
                                  Görüntüle
                                </Button>
                              )}
                              {/* İndir butonu */}
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  const fileSrc = doc.url || doc.data;
                                  const fileName = doc.name || doc.filename || 'belge';
                                  
                                  // Base64 ise blob'a çevir
                                  if (fileSrc.startsWith('data:')) {
                                    try {
                                      const byteString = atob(fileSrc.split(',')[1]);
                                      const mimeType = fileSrc.match(/data:([^;]+);/)?.[1] || 'application/octet-stream';
                                      const ab = new ArrayBuffer(byteString.length);
                                      const ia = new Uint8Array(ab);
                                      for (let i = 0; i < byteString.length; i++) {
                                        ia[i] = byteString.charCodeAt(i);
                                      }
                                      const blob = new Blob([ab], { type: mimeType });
                                      const url = URL.createObjectURL(blob);
                                      const link = document.createElement('a');
                                      link.href = url;
                                      link.download = fileName;
                                      document.body.appendChild(link);
                                      link.click();
                                      document.body.removeChild(link);
                                      URL.revokeObjectURL(url);
                                    } catch (e) {
                                      console.error('Download error:', e);
                                      window.open(fileSrc, '_blank');
                                    }
                                  } else {
                                    const link = document.createElement('a');
                                    link.href = fileSrc;
                                    link.download = fileName;
                                    document.body.appendChild(link);
                                    link.click();
                                    document.body.removeChild(link);
                                  }
                                }}
                                className="text-green-600 hover:text-green-700"
                                data-testid={`download-doc-${idx}`}
                              >
                                <Download className="h-4 w-4 mr-1" />
                                İndir
                              </Button>
                            </>
                          )}
                          {/* URL veya data yoksa uyarı göster */}
                          {!doc.url && !doc.data && (
                            <span className="text-xs text-gray-400">Belge verisi yok</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Onay Bilgisi */}
              {selectedRequest.status === 'approved' && (
                <div className="pt-4 border-t bg-green-50 p-3 rounded-lg">
                  <Label className="text-green-700">Onaylayan</Label>
                  <p className="font-medium text-green-800">{selectedRequest.approved_by_name}</p>
                  <p className="text-sm text-green-600">{formatDate(selectedRequest.approved_at)}</p>
                  {selectedRequest.approval_note && (
                    <p className="text-sm mt-2">{selectedRequest.approval_note}</p>
                  )}
                </div>
              )}
              
              {/* Red Bilgisi */}
              {selectedRequest.status === 'rejected' && (
                <div className="pt-4 border-t bg-red-50 p-3 rounded-lg">
                  <Label className="text-red-700">Reddeden</Label>
                  <p className="font-medium text-red-800">{selectedRequest.rejected_by_name}</p>
                  <p className="text-sm text-red-600">{formatDate(selectedRequest.rejected_at)}</p>
                  {selectedRequest.rejection_reason && (
                    <p className="text-sm mt-2">{selectedRequest.rejection_reason}</p>
                  )}
                </div>
              )}
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            {selectedRequest && (selectedRequest.status === 'pending' || selectedRequest.status === 'pending_muhasebe' || selectedRequest.status === 'forwarded_to_accounting') && (
              <div className="flex flex-wrap gap-2 w-full sm:w-auto">
                <Button
                  variant="outline"
                  className="flex-1 sm:flex-none text-indigo-600 border-indigo-600 hover:bg-indigo-50"
                  onClick={() => {
                    setShowDetailModal(false);
                    openAddToJobsModal(selectedRequest);
                  }}
                  data-testid="add-to-jobs-btn"
                >
                  <Briefcase className="h-4 w-4 mr-2" />
                  İş Olarak Ekle
                </Button>
                <Button
                  variant="outline"
                  className="flex-1 sm:flex-none text-green-600 border-green-600 hover:bg-green-50"
                  onClick={() => {
                    setShowDetailModal(false);
                    setActionType('approve');
                    setShowActionModal(true);
                  }}
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Onayla
                </Button>
                <Button
                  variant="outline"
                  className="flex-1 sm:flex-none text-red-600 border-red-600 hover:bg-red-50"
                  onClick={() => {
                    setShowDetailModal(false);
                    setActionType('reject');
                    setShowActionModal(true);
                  }}
                >
                  <XCircle className="h-4 w-4 mr-2" />
                  Reddet
                </Button>
              </div>
            )}
            <Button onClick={() => setShowDetailModal(false)}>Kapat</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Action Modal (Approve/Reject) */}
      <Dialog open={showActionModal} onOpenChange={setShowActionModal}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className={`flex items-center gap-2 ${actionType === 'approve' ? 'text-green-600' : 'text-red-600'}`}>
              {actionType === 'approve' ? <CheckCircle className="h-5 w-5" /> : <XCircle className="h-5 w-5" />}
              {actionType === 'approve' ? 'Talebi Onayla' : 'Talebi Reddet'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {selectedRequest && (
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="font-medium">{selectedRequest.title}</p>
                <p className="text-sm text-gray-500">{selectedRequest.requester_name}</p>
              </div>
            )}
            <div className="space-y-2">
              <Label>{actionType === 'approve' ? 'Not (Opsiyonel)' : 'Reddetme Sebebi *'}</Label>
              <Textarea 
                value={actionNote}
                onChange={(e) => setActionNote(e.target.value)}
                placeholder={actionType === 'approve' ? 'Onay notu ekleyin...' : 'Reddetme sebebini yazın...'}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowActionModal(false); setActionNote(''); }}>İptal</Button>
            <Button 
              onClick={handleAction}
              className={actionType === 'approve' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
            >
              {actionType === 'approve' ? 'Onayla' : 'Reddet'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* İş Olarak Ekle Modal - AccountingJobs formatında */}
      <Dialog open={showAddToJobsModal} onOpenChange={setShowAddToJobsModal}>
        <DialogContent className="sm:max-w-[550px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-indigo-600">
              <Briefcase className="h-5 w-5" />
              İş Olarak Ekle
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            {selectedRequest && (
              <div className="p-3 bg-indigo-50 rounded-lg border border-indigo-200">
                <p className="text-sm text-indigo-600 font-medium">Kaynak Talep:</p>
                <p className="font-medium text-indigo-800">{selectedRequest.title}</p>
                <p className="text-sm text-indigo-600">{selectedRequest.requester_name || selectedRequest.owner_name}</p>
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="job-konu">İşin Konusu *</Label>
              <Input
                id="job-konu"
                value={jobFormData.konu}
                onChange={(e) => setJobFormData(prev => ({ ...prev, konu: e.target.value }))}
                placeholder="İşin konusunu girin"
                data-testid="job-konu-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="job-aciklama">Açıklama</Label>
              <Textarea
                id="job-aciklama"
                value={jobFormData.aciklama}
                onChange={(e) => setJobFormData(prev => ({ ...prev, aciklama: e.target.value }))}
                placeholder="İş açıklamasını girin"
                rows={3}
                data-testid="job-aciklama-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="job-sube">Şube</Label>
              <Select 
                value={jobFormData.sube_id} 
                onValueChange={(value) => setJobFormData(prev => ({ ...prev, sube_id: value }))}
              >
                <SelectTrigger data-testid="job-sube-select">
                  <SelectValue placeholder="Şube seçin" />
                </SelectTrigger>
                <SelectContent>
                  {branches.map((branch) => (
                    <SelectItem key={branch.id} value={branch.id}>
                      {branch.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Belgeler Bölümü */}
            <div className="space-y-2">
              <Label>Belgeler</Label>
              
              {/* Yüklü belgeler */}
              {jobFormData.belgeler && jobFormData.belgeler.length > 0 && (
                <div className="space-y-2 mb-2">
                  {jobFormData.belgeler.map((doc, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded border">
                      <div className="flex items-center gap-2">
                        <File className="h-4 w-4 text-gray-500" />
                        <span className="text-sm truncate max-w-[200px]">{doc.name}</span>
                        {doc.size && (
                          <span className="text-xs text-gray-400">
                            ({(doc.size / 1024).toFixed(1)} KB)
                          </span>
                        )}
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeJobFile(idx)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Dosya yükleme butonu */}
              <div className="flex items-center gap-2">
                <input
                  type="file"
                  id="job-file-upload"
                  className="hidden"
                  multiple
                  onChange={handleJobFileUpload}
                  accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => document.getElementById('job-file-upload').click()}
                  className="gap-2"
                >
                  <Upload className="h-4 w-4" />
                  Belge Ekle
                </Button>
                <span className="text-xs text-gray-500">
                  (Resim, PDF, Word, Excel)
                </span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowAddToJobsModal(false)}
            >
              İptal
            </Button>
            <Button 
              onClick={handleAddToJobs}
              disabled={addToJobsProcessing || !jobFormData.konu.trim()}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="confirm-add-to-jobs-btn"
            >
              {addToJobsProcessing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Ekleniyor...
                </>
              ) : (
                <>
                  <Briefcase className="h-4 w-4 mr-2" />
                  İşlere Ekle
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Talep Geçmişi Modal */}
      <Dialog open={showHistoryModal} onOpenChange={setShowHistoryModal}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-indigo-600">
              <History className="h-5 w-5" />
              Talep Geçmişi
            </DialogTitle>
          </DialogHeader>
          {selectedRequest && (
            <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
              {/* Orijinal Talep Bilgileri */}
              <div className="p-4 bg-gray-50 rounded-lg border">
                <h4 className="font-semibold text-gray-800 mb-3">Orijinal Talep Detayları</h4>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-500 text-xs">Talebin Konusu</Label>
                    <p className="font-medium">{selectedRequest.title || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-gray-500 text-xs">Talep Eden</Label>
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-gray-400" />
                      <p className="font-medium">{selectedRequest.requester_name || selectedRequest.owner_name || '-'}</p>
                    </div>
                  </div>
                  <div>
                    <Label className="text-gray-500 text-xs">Şube</Label>
                    <div className="flex items-center gap-2">
                      <Building className="h-4 w-4 text-gray-400" />
                      <p className="font-medium">{selectedRequest.branch_name || '-'}</p>
                    </div>
                  </div>
                  <div>
                    <Label className="text-gray-500 text-xs">Oluşturulma Tarihi</Label>
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-gray-400" />
                      <div>
                        <p className="font-medium">{formatDateTime(selectedRequest.created_at).date}</p>
                        <p className="text-xs text-gray-500">{formatDateTime(selectedRequest.created_at).time}</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                {selectedRequest.description && (
                  <div className="mt-4">
                    <Label className="text-gray-500 text-xs">Açıklama</Label>
                    <p className="text-sm text-gray-700 mt-1">{selectedRequest.description}</p>
                  </div>
                )}
              </div>
              
              {/* İşe Dönüştürme Bilgileri */}
              {selectedRequest.converted_at && (
                <div className="p-4 bg-indigo-50 rounded-lg border border-indigo-200">
                  <h4 className="font-semibold text-indigo-800 mb-3">İşe Dönüştürme Bilgileri</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-indigo-600 text-xs">Dönüştüren</Label>
                      <p className="font-medium text-indigo-800">{selectedRequest.converted_by_name || '-'}</p>
                    </div>
                    <div>
                      <Label className="text-indigo-600 text-xs">Dönüştürme Tarihi</Label>
                      <div>
                        <p className="font-medium text-indigo-800">{formatDateTime(selectedRequest.converted_at).date}</p>
                        <p className="text-xs text-indigo-600">{formatDateTime(selectedRequest.converted_at).time}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Belgeler */}
              {((selectedRequest.attachments && selectedRequest.attachments.length > 0) || 
                (selectedRequest.documents && selectedRequest.documents.length > 0)) && (
                <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <h4 className="font-semibold text-amber-800 mb-3 flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Belgeler ({(selectedRequest.attachments?.length || 0) + (selectedRequest.documents?.length || 0)})
                  </h4>
                  <div className="space-y-2">
                    {[...(selectedRequest.attachments || []), ...(selectedRequest.documents || [])].map((doc, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-white rounded-lg border">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-amber-100 rounded-lg">
                            <FileText className="h-5 w-5 text-amber-600" />
                          </div>
                          <div>
                            <span className="text-sm font-medium block">{doc.name || doc.filename || 'Belge'}</span>
                            {doc.size && (
                              <span className="text-xs text-gray-400">
                                {(doc.size / 1024).toFixed(1)} KB
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {(doc.url || doc.data) && (
                            <>
                              {/* Önizle butonu - sadece resimler için */}
                              {(doc.type?.startsWith('image/') || doc.name?.match(/\.(jpg|jpeg|png|gif|webp)$/i)) && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    const imgSrc = doc.url || doc.data;
                                    const modal = document.createElement('div');
                                    modal.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,0.8);display:flex;align-items:center;justify-content:center;z-index:9999;cursor:pointer';
                                    modal.onclick = () => modal.remove();
                                    const img = document.createElement('img');
                                    img.src = imgSrc;
                                    img.style.cssText = 'max-width:90%;max-height:90%;object-fit:contain;border-radius:8px';
                                    modal.appendChild(img);
                                    document.body.appendChild(modal);
                                  }}
                                  className="text-blue-600 hover:text-blue-700"
                                >
                                  <Eye className="h-4 w-4 mr-1" />
                                  Önizle
                                </Button>
                              )}
                              {/* PDF için görüntüle */}
                              {(doc.type === 'application/pdf' || doc.name?.match(/\.pdf$/i)) && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    const pdfSrc = doc.url || doc.data;
                                    window.open(pdfSrc, '_blank');
                                  }}
                                  className="text-blue-600 hover:text-blue-700"
                                >
                                  <Eye className="h-4 w-4 mr-1" />
                                  Görüntüle
                                </Button>
                              )}
                              {/* İndir butonu */}
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  const fileSrc = doc.url || doc.data;
                                  const fileName = doc.name || doc.filename || 'belge';
                                  
                                  if (fileSrc.startsWith('data:')) {
                                    try {
                                      const byteString = atob(fileSrc.split(',')[1]);
                                      const mimeType = fileSrc.match(/data:([^;]+);/)?.[1] || 'application/octet-stream';
                                      const ab = new ArrayBuffer(byteString.length);
                                      const ia = new Uint8Array(ab);
                                      for (let i = 0; i < byteString.length; i++) {
                                        ia[i] = byteString.charCodeAt(i);
                                      }
                                      const blob = new Blob([ab], { type: mimeType });
                                      const url = URL.createObjectURL(blob);
                                      const link = document.createElement('a');
                                      link.href = url;
                                      link.download = fileName;
                                      document.body.appendChild(link);
                                      link.click();
                                      document.body.removeChild(link);
                                      URL.revokeObjectURL(url);
                                    } catch (e) {
                                      console.error('Download error:', e);
                                      window.open(fileSrc, '_blank');
                                    }
                                  } else {
                                    const link = document.createElement('a');
                                    link.href = fileSrc;
                                    link.download = fileName;
                                    document.body.appendChild(link);
                                    link.click();
                                    document.body.removeChild(link);
                                  }
                                }}
                                className="text-green-600 hover:text-green-700"
                              >
                                <Download className="h-4 w-4 mr-1" />
                                İndir
                              </Button>
                            </>
                          )}
                          {!doc.url && !doc.data && (
                            <span className="text-xs text-gray-400">Belge verisi yok</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setShowHistoryModal(false)}>Kapat</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AccountingRequests;
